import { useEffect, useMemo, useState } from "react";
import { initializeCollections, addCollection, updateCollection, deleteCollection } from "../redux/actions/collectionActions";
import { useAppDispatch } from "../redux/hooks";
import { useSelector } from "react-redux";
import {
    MantineReactTable,
    useMantineReactTable,
    type MRT_ColumnDef,
    type MRT_SortingState,
} from 'mantine-react-table';
import {
    ActionIcon,
    Box,
    Button,
    Flex,
    Group,
    Select,
    Text,
    Title,
    Tooltip,
} from '@mantine/core';
import {
    IconEdit,
    IconTrash,
    IconPlus,
    IconChevronLeft,
    IconChevronRight,
    IconChevronsLeft,
    IconChevronsRight
} from '@tabler/icons-react';
import { convertDateTimeToLocalTime } from "../infrastructures/utils";
import { CollectionResponse } from "../models/responses/CollectionResponse";
import { useAuth } from "../customAuth/authStateHook";
import { CollectionModal } from "../components/CollectionModal";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import { toast } from "react-toastify";

const CollectionsView = () => {
    const { collections, isLoading } = useSelector((state: any) => state.collection);
    const dispatch = useAppDispatch();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [globalFilter, setGlobalFilter] = useState('');
    const [sorting, setSorting] = useState<MRT_SortingState>([]);
    const { isSuperAdmin } = useAuth();

    const [collectionModel, setCollectionModel] = useState<CollectionResponse | null>(null);
    const [showCollectionModal, setShowCollectionModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedCollectionId, setSelectedCollectionId] = useState<number | null>(null);
    const [inProgress, setInProgress] = useState(false);

    const filteredCollections = collections.filter((collection: CollectionResponse) => {
        if (!globalFilter) return true;
        const searchLower = globalFilter.toLowerCase();
        return (
            collection.Name.toLowerCase().includes(searchLower)
        );
    });

    const toTime = (value?: Date) => {
        if (!value) return 0;
        const time = new Date(value).getTime();
        return Number.isNaN(time) ? 0 : time;
    };

    const sortedCollections = useMemo(() => {
        if (sorting.length === 0) return filteredCollections;
        const { id, desc } = sorting[0];
        const sorted = [...filteredCollections].sort((a, b) => {
            let cmp = 0;
            if (id === 'dateCreatedUtc') {
                cmp = toTime(a.DateCreatedUtc) - toTime(b.DateCreatedUtc);
            } else if (id === 'dateUpdatedUtc') {
                cmp = toTime(a.DateUpdatedUtc) - toTime(b.DateUpdatedUtc);
            } else {
                cmp = (a.Name ?? '').localeCompare(b.Name ?? '');
            }
            return desc ? -cmp : cmp;
        });
        return sorted;
    }, [filteredCollections, sorting]);

    const totalItems = sortedCollections.length;
    const totalPages = Math.ceil(totalItems / pageSize) || 1;
    const from = totalItems > 0 ? (page - 1) * pageSize + 1 : 0;
    const to = Math.min(page * pageSize, totalItems);
    const paginatedCollections = sortedCollections.slice((page - 1) * pageSize, page * pageSize);

    useEffect(() => {
        dispatch(initializeCollections());
    }, [dispatch]);

    useEffect(() => {
        if (page > totalPages && totalPages > 0) {
            setPage(1);
        }
    }, [page, totalPages]);

    const columns = useMemo<MRT_ColumnDef<CollectionResponse>[]>(() => [
        { accessorKey: 'Name', header: 'Name' },
        {
            accessorKey: 'dateCreatedUtc',
            header: 'Date Created',
            accessorFn: (row) => convertDateTimeToLocalTime(row.DateCreatedUtc),
        },
        {
            accessorKey: 'dateUpdatedUtc',
            header: 'Date Updated',
            accessorFn: (row) => convertDateTimeToLocalTime(row.DateUpdatedUtc),
        },
    ], []);

    const handleDelete = async () => {
        if (selectedCollectionId) {
            const collectionToDelete = collections.find((c: CollectionResponse) => c.Id === selectedCollectionId);
            if (!collectionToDelete) {
                toast.error('Collection not found');
                setShowDeleteModal(false);
                setSelectedCollectionId(null);
                return;
            }

            if (collectionToDelete?.IsSourceCollection || collectionToDelete?.IsSystemCollection) {
                toast.error('Source or system collection cannot be deleted');
                setShowDeleteModal(false);
                setSelectedCollectionId(null);
                return;
            }

            const result = await dispatch(deleteCollection(selectedCollectionId));
            if (result.success) {
                setShowDeleteModal(false);
                setSelectedCollectionId(null);
            }
        }
    };

    const handleCollectionSubmit = async (values: any) => {
        setInProgress(true);
        if (collectionModel) {
            // Update existing collection
            const updatedCollection: CollectionResponse = {
                ...collectionModel,
                Name: values.name
            };
            const result = await dispatch(updateCollection(updatedCollection));
            if (result.success) {
                setShowCollectionModal(false);
                setCollectionModel(null);
            }
        } else {
            // Add new collection
            const result = await dispatch(addCollection({ name: values.name }));
            if (result.success) {
                setShowCollectionModal(false);
                setCollectionModel(null);
            }
        }
        setInProgress(false);
    };

    const table = useMantineReactTable({
        columns,
        data: paginatedCollections,
        manualPagination: true,
        manualSorting: true,
        pageCount: totalPages,
        state: {
            isLoading: isLoading,
            globalFilter,
            sorting,
        },
        onSortingChange: (updater) => {
            setSorting(updater);
            setPage(1);
        },
        onGlobalFilterChange: (filter) => {
            setGlobalFilter(filter || '');
            setPage(1);
        },
        enableRowActions: isSuperAdmin,
        positionActionsColumn: 'last',
        enableExpandAll: false,
        renderBottomToolbarCustomActions: () => (
            <Flex justify="space-between" align="center" gap="sm" p="sm" bg="var(--color-grey-mauve)" wrap="wrap" w="100%" className="collections-pagination">
                <Flex align="center" gap="sm" wrap="wrap">
                    <Text size="sm">Rows per page</Text>

                    <Select
                        value={pageSize.toString()}
                        onChange={(value) => {
                            setPageSize(Number(value));
                            setPage(1);
                        }}
                        radius="xs"
                        data={['5', '10', '20', '50', '100']}
                        w={80}
                        size="xs"
                    />

                    <Text size="sm">
                        {from}-{to} of {totalItems}{globalFilter && filteredCollections.length !== collections.length ? ` (filtered from ${collections.length})` : ''}
                    </Text>
                </Flex>

                <Flex align="center" gap="xs">
                    <Button variant="default" size="xs" radius="xs" disabled={page === 1} onClick={() => setPage(1)} className="pagination-button">
                        <IconChevronsLeft size={16} />
                    </Button>

                    <Button variant="default" size="xs" radius="xs" disabled={page === 1} onClick={() => setPage((prev) => Math.max(prev - 1, 1))} className="pagination-button">
                        <IconChevronLeft size={16} />
                    </Button>

                    <Button variant="default" size="xs" radius="xs" disabled={page === totalPages} onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))} className="pagination-button">
                        <IconChevronRight size={16} />
                    </Button>

                    <Button variant="default" size="xs" radius="xs" disabled={page === totalPages} onClick={() => setPage(totalPages)} className="pagination-button">
                        <IconChevronsRight size={16} />
                    </Button>
                </Flex>
            </Flex>
        ),
        renderRowActions: ({ row }) => (
            <Group gap="xs" wrap="nowrap">
                <Tooltip label="Edit">
                    <ActionIcon
                        variant="light"
                        color="#8a6a4a"
                        onClick={() => {
                            setCollectionModel(row.original);
                            setShowCollectionModal(true);
                        }}
                    >
                        <IconEdit size={18} />
                    </ActionIcon>
                </Tooltip>
                <Tooltip label="Delete">
                    <ActionIcon
                        variant="light"
                        color="#8a6a4a"
                        disabled={row.original.IsSystemCollection || row.original.IsSourceCollection}
                        onClick={() => {
                            setSelectedCollectionId(row.original.Id);
                            setShowDeleteModal(true);
                        }}
                    >
                        <IconTrash size={18} />
                    </ActionIcon>
                </Tooltip>
            </Group>
        ),

        enableGlobalFilter: true,
        enableColumnFilters: false,
        enableSorting: true,
        enablePagination: false,

        enableColumnActions: false,
        enableColumnOrdering: false,
        enableFullScreenToggle: true,

        renderTopToolbarCustomActions: ({ table }) => (
            <Flex justify="flex-end" w="100%">
                {table.getState().density !== undefined &&
                    table.options.renderToolbarInternalActions?.({ table })}
            </Flex>
        ),

        mantineSearchTextInputProps: {
            placeholder: 'Search collection...',
            style: { maxWidth: '300px' },
        },

        mantinePaperProps: {
            className: 'mindvault-table-paper',
        },

        mantineTableContainerProps: {
            style: { overflowX: 'auto' },
        },

        mantineTableProps: {
            highlightOnHover: true,
            withColumnBorders: false,
            verticalSpacing: 'xs',
            style: { minWidth: '820px' },
        },
    });

    return (
        <Box p="xl" className="collections-container">
            <Flex mb="lg" align="center" justify="space-between">
                <Box>
                    <Title fw={700} order={2} className="page-title">
                        Collections
                    </Title>
                    <Text size="sm" c="dimmed">{totalItems} collections</Text>
                </Box>
                {isSuperAdmin && (
                    <Button
                        color="#151514"
                        leftSection={<IconPlus size={16} />}
                        onClick={() => {
                            setCollectionModel(null);
                            setShowCollectionModal(true);
                        }}
                    >
                        Add Collection
                    </Button>
                )}
            </Flex>

            <MantineReactTable table={table} />

            <CollectionModal
                key={collectionModel?.Id ?? 'new'}
                opened={showCollectionModal}
                onClose={() => {
                    setShowCollectionModal(false);
                    setCollectionModel(null);
                }}
                onSubmit={handleCollectionSubmit}
                initialValues={collectionModel}
                isLoading={inProgress}
            />

            <DeleteConfirmationModal
                dialogVisible={showDeleteModal}
                dialogHandlers={{
                    close: () => {
                        setShowDeleteModal(false);
                        setSelectedCollectionId(null);
                    }
                }}
                onConfirm={handleDelete}
                message="Are you sure you want to delete this collection?"
            />
        </Box>
    )
}

export default CollectionsView;

